import type { QueryClient } from '@tanstack/react-query';

import { logger } from '@/lib/logger';
import { track } from '@/lib/mixpanel';
import type {
  ChatProgressData,
  ChatRunContext,
  ChatRunHandlers,
  ChatRunPollClient,
} from '@/lib/reasoning/realtime/chatProgressPoll';
import { createDefaultPollClient } from '@/lib/reasoning/realtime/chatProgressPoll';
import {
  __resetChatRunTrackerForTesting,
  __setPollClientForTesting,
  isRunActive,
  MAX_TRACKERS,
  onChatRunUpdatedWs as onChatRunUpdatedWsCore,
  registerChatRun,
  setRunHidden,
  setRunVisible,
  subscribeChatRuns,
} from '@/lib/reasoning/realtime/chatRunTracker';

import { createProposalClient } from '../core/ProposalClient';
import type { ProposalDetail } from '../core/types';
import { proposalQueryKeys } from '../query/keys';
import { showChatTurnCompleteToast } from '../utils/showChatTurnCompleteToast';
import { showExecutionFailureToast } from '../utils/showExecutionFailureToast';

const runKey = (proposalId: number): string => `proposal:${proposalId}`;

interface ChatTrackerClient {
  fetchChatProgress(
    conversationId: number,
    taskId: string
  ): Promise<ChatProgressData>;
  getProposalDetail(proposalId: number): Promise<ProposalDetail>;
}

function createDefaultChatTrackerClient(orgId: number): ChatTrackerClient {
  return {
    async fetchChatProgress(conversationId, taskId) {
      return createDefaultPollClient(String(orgId)).fetchChatProgress(
        String(conversationId),
        taskId
      );
    },
    async getProposalDetail(proposalId) {
      return createProposalClient(orgId).get(proposalId);
    },
  };
}

let clientFactory = createDefaultChatTrackerClient;

function makeProposalHandlers(args: {
  orgId: number;
  proposalId: number;
  conversationId: number;
  queryClient: QueryClient;
}): ChatRunHandlers {
  const { orgId, proposalId, conversationId, queryClient } = args;

  return {
    async onComplete(ctx: ChatRunContext) {
      try {
        const detail = await clientFactory(orgId).getProposalDetail(proposalId);
        queryClient.setQueryData(
          proposalQueryKeys.detail(orgId, proposalId),
          detail
        );
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.discussionConversation(
            orgId,
            conversationId
          ),
        });
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.lists(orgId),
        });
        track('proposal_chat_turn_outcome', {
          proposal_id: proposalId,
          outcome: 'success',
        });
        if (!ctx.suppressToast) {
          showChatTurnCompleteToast({
            orgId,
            proposalId,
            proposalTitle: detail.title,
          });
        }
      } catch (err) {
        logger.error(
          '[chatTaskTracker] could not fetch detail after chat turn',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'proposals', proposalId, pollCount: ctx.pollCount }
        );
        // The turn DID complete — we just couldn't fetch the fresh detail to write
        // into the cache. Invalidate so the next read refetches instead of showing
        // the stale in-flight detail until its stale window elapses.
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.detail(orgId, proposalId),
        });
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.discussionConversation(
            orgId,
            conversationId
          ),
        });
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.lists(orgId),
        });
        track('proposal_chat_turn_outcome', {
          proposal_id: proposalId,
          outcome: 'unknown',
        });
        showExecutionFailureToast({
          orgId,
          proposalId,
          message:
            'Couldn’t confirm the assistant finished. Open the action to check.',
        });
      }
    },
    onFailed(
      ctx: ChatRunContext,
      reason: 'failed' | 'timeout',
      serverMessage?: string
    ) {
      if (reason === 'failed') {
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.discussionConversation(
            orgId,
            conversationId
          ),
        });
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.lists(orgId),
        });
        logger.warn('[chatTaskTracker] chat turn failed', {
          component: 'proposals',
          proposalId,
          pollCount: ctx.pollCount,
        });
      } else {
        logger.error(
          '[chatTaskTracker] chat turn watch timed out',
          new Error('chat watch ceiling reached'),
          { component: 'proposals', proposalId, pollCount: ctx.pollCount }
        );
      }
      track('proposal_chat_turn_outcome', {
        proposal_id: proposalId,
        outcome: reason === 'timeout' ? 'timeout' : 'failure',
      });
      const trimmed = serverMessage?.trim();
      const failedMessage = trimmed
        ? trimmed
        : 'The assistant couldn’t finish updating this action.';
      showExecutionFailureToast({
        orgId,
        proposalId,
        message:
          reason === 'timeout'
            ? 'This is taking longer than expected. Open the action to check its status.'
            : failedMessage,
      });
    },
    onSuperseded(ctx) {
      logger.debug('[chatTaskTracker] superseding turn', {
        component: 'proposals',
        proposalId,
        oldTaskId: ctx.oldTaskId,
        newTaskId: ctx.newTaskId,
      });
    },
    onEvicted(ctx) {
      const keyPrefix = 'proposal:';
      let evictedProposalId: number | string = ctx.evictedKey;
      if (ctx.evictedKey.startsWith(keyPrefix)) {
        const parsed = Number(ctx.evictedKey.slice(keyPrefix.length));
        if (!Number.isNaN(parsed)) {
          evictedProposalId = parsed;
        }
      }
      logger.warn(
        '[chatTaskTracker] max trackers reached — evicting oldest to make room',
        {
          component: 'proposals',
          proposalId,
          evictedProposalId,
          maxTrackers: MAX_TRACKERS,
        }
      );
    },
    onPollFailed() {
      logger.warn('[chatTaskTracker] poll failed, will retry until watchdog', {
        component: 'proposals',
        proposalId,
      });
    },
  };
}

export function trackChatTurn({
  orgId,
  proposalId,
  conversationId,
  taskId,
  queryClient,
}: {
  orgId: number;
  proposalId: number;
  conversationId: number;
  taskId: string;
  queryClient: QueryClient;
}): void {
  registerChatRun({
    key: runKey(proposalId),
    orgId: String(orgId),
    conversationId: String(conversationId),
    taskId,
    // A proposal chat turn ends by pausing the chain for the user's review, so
    // a `paused` status is this run's completion signal.
    treatPausedAsComplete: true,
    handlers: makeProposalHandlers({
      orgId,
      proposalId,
      conversationId,
      queryClient,
    }),
  });
}

export const subscribeChatTracker = subscribeChatRuns;

export function isChatTurnTracked(proposalId: number): boolean {
  return isRunActive(runKey(proposalId));
}

export function setProposalVisible(proposalId: number): void {
  setRunVisible(runKey(proposalId));
}

export function setProposalHidden(proposalId: number): void {
  setRunHidden(runKey(proposalId));
}

export function onChatTurnUpdatedWs(proposalId: number, taskId: string): void {
  onChatRunUpdatedWsCore(runKey(proposalId), taskId);
}

export function __setClientFactoryForTesting(
  fn: (orgId: number) => ChatTrackerClient
): void {
  clientFactory = fn;
  __setPollClientForTesting(
    (orgId: string): ChatRunPollClient => ({
      fetchChatProgress: (conversationId, taskId) =>
        fn(Number(orgId)).fetchChatProgress(Number(conversationId), taskId),
    })
  );
}

export function __resetForTesting(): void {
  clientFactory = createDefaultChatTrackerClient;
  __resetChatRunTrackerForTesting();
}
